import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Check, Lock, Shield, Crown, Zap,
  AlertCircle, Loader2, Sparkles, BookOpen,
} from 'lucide-react';
import { BACKEND_URL } from '../config';
import { useAuth } from '../context/AuthContext';

const PLAN_DETAILS = {
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 699,
    original: 999,
    period: 'month',
    icon: Zap,
    accent: 'from-blue-600 to-blue-500',
    chip: 'bg-blue-100 text-blue-700',
    features: [
      '8 full-length mocks per subject / month',
      'Up to 40 mocks across 5 subjects',
      'Full national leaderboard + rank badge',
      'AI weak-area analysis & heatmap',
      'Question-by-question AI feedback',
      'Topic-wise performance reports',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    price: 999,
    original: 1499,
    period: 'month',
    icon: Crown,
    accent: 'from-amber-500 to-orange-500',
    chip: 'bg-amber-100 text-amber-700',
    features: [
      '10 full-length mocks per subject / month',
      'Up to 50 mocks across 5 subjects',
      'Full national leaderboard + rank badge',
      'AI weak-area analysis & heatmap',
      'College rank prediction (SRCC, Stephen\'s)',
      'Compare your answers with toppers',
    ],
  },
};

// ─── Cashfree loader ─────────────────────────────────────────────────────────
const loadCashfree = () =>
  new Promise((resolve) => {
    if (window.Cashfree) return resolve(new window.Cashfree({ mode: 'production' }));
    const s = document.createElement('script');
    s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    s.onload = () => resolve(new window.Cashfree({ mode: 'production' }));
    document.head.appendChild(s);
  });

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const planId = (searchParams.get('plan') || 'pro').toLowerCase();
  const plan = PLAN_DETAILS[planId] || PLAN_DETAILS.pro;
  const Icon = plan.icon;

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If invalid plan param, default to pro in URL
    if (!PLAN_DETAILS[planId]) {
      navigate('/checkout?plan=pro', { replace: true });
    }
  }, [planId, navigate]);

  const savings = useMemo(() => plan.original - plan.price, [plan]);

  const clearError = () => {
    if (error) setError(null);
  };

  const validate = () => {
    if (!name.trim() || name.trim().length < 2) return 'Please enter your full name.';
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return 'Please enter a valid email address.';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return 'Please enter a valid 10-digit phone number.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/payment/checkout-register`, {
        plan: plan.id,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/\D/g, '').slice(-10),
      });
      if (!data.success) {
        setError(data.message || 'Could not start checkout. Please try again.');
        setSubmitting(false);
        return;
      }
      const { payment_session_id } = data.data;
      const cashfree = await loadCashfree();
      cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: '_self' });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Could not start checkout. Please try again or email info@crackcuet.com';
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Top Nav ─────────────────────────────────────────── */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button
            data-testid="checkout-back-btn"
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to plans
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">Crack CUET</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Lock className="h-3.5 w-3.5" />
            Secure Checkout
          </div>
        </div>
      </nav>

      {/* ── Page Content ───────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* ── LEFT: Form ───────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">Step 1 of 1</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">
              Complete your purchase
            </h1>
            <p className="text-slate-500 text-sm mb-7">
              No password needed — we'll email you a secure access link right after payment.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="checkout-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Full name
                </label>
                <input
                  data-testid="checkout-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); clearError(); }}
                  placeholder="Aanya Sharma"
                  autoComplete="name"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 placeholder:text-slate-400 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Email address
                </label>
                <input
                  data-testid="checkout-email-input"
                  type="text"
                  inputMode="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 placeholder:text-slate-400 transition"
                />
                <p className="text-xs text-slate-400 mt-1.5">
                  Your access link & receipt will be sent here.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Phone number
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-sm font-medium">
                    +91
                  </span>
                  <input
                    data-testid="checkout-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); clearError(); }}
                    placeholder="9876543210"
                    autoComplete="tel"
                    inputMode="numeric"
                    className="flex-1 px-4 py-3 rounded-r-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 placeholder:text-slate-400 transition"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">
                  Required by payment gateway. We won't spam you.
                </p>
              </div>

              {error && (
                <div
                  data-testid="checkout-error-msg"
                  className="p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5 text-sm"
                >
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <button
                data-testid="checkout-pay-btn"
                type="submit"
                disabled={submitting}
                className={`w-full py-4 rounded-xl font-bold text-base text-white transition-all bg-gradient-to-r ${plan.accent} hover:opacity-95 shadow-lg disabled:opacity-70`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Redirecting to secure payment…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Lock className="h-4 w-4" />
                    Pay ₹{plan.price} & unlock {plan.name}
                  </span>
                )}
              </button>

              <div className="flex items-center justify-center gap-4 text-xs text-slate-400 pt-2">
                <span className="flex items-center gap-1">
                  <Shield className="h-3.5 w-3.5" /> 100% secure
                </span>
                <span>•</span>
                <span>Powered by Cashfree</span>
                <span>•</span>
                <span>UPI / Cards / Netbanking</span>
              </div>
            </form>
          </div>

          {/* ── RIGHT: Order Summary ─────────────────────── */}
          <div className="lg:sticky lg:top-6 self-start">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className={`bg-gradient-to-r ${plan.accent} p-6 text-white`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-widest opacity-80">
                    Order Summary
                  </span>
                  <Icon className="h-5 w-5 opacity-80" />
                </div>
                <h2 className="text-3xl font-extrabold">Crack CUET {plan.name}</h2>
                <p className="text-white/80 text-sm mt-1">Valid for 30 days from purchase</p>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                    What you get
                  </p>
                  <ul className="space-y-2.5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-dashed border-slate-200 pt-5 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="text-slate-700">₹{plan.original}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      <Sparkles className="h-3.5 w-3.5" />
                      Launch discount
                    </span>
                    <span className="text-green-600 font-semibold">−₹{savings}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <span className="font-bold text-slate-900">Total payable today</span>
                    <span className="text-2xl font-extrabold text-slate-900">₹{plan.price}</span>
                  </div>
                  <p className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${plan.chip}`}>
                    You save ₹{savings} • {Math.round((savings / plan.original) * 100)}% off
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 leading-relaxed">
                  <p>
                    <strong className="text-slate-700">No auto-renewal.</strong> One-time payment for 30 days. After expiry, your account moves back to Free — renew only when you want to.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400 mt-4">
              By continuing, you agree to our{' '}
              <button onClick={() => navigate('/terms')} className="underline hover:text-slate-600">Terms</button>
              {' '}and{' '}
              <button onClick={() => navigate('/refund-policy')} className="underline hover:text-slate-600">Refund Policy</button>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
